import React from 'react'
import { Stack, Typography, Box, Tooltip } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'

import ActionToggle from './ActionToggle'
import RoleItemCheckbox  from './RoleItemCheckbox'
type RoleState = 'add' | 'remove' | 'unchanged'

interface BaseRoleItemProps {
  role: string
  disabled?: boolean
  padding?: string
  compact?: boolean
  colorScheme?: 'blue' | 'green'
  isGeneral?: boolean
}

interface EditModeRoleItemProps extends BaseRoleItemProps {
  mode: 'edit'
  checked: boolean
}

interface ManagementModeRoleItemProps extends BaseRoleItemProps {
  mode: 'management'
  state: RoleState
  onChange: (role: string, newState: RoleState) => void
}

type RoleItemProps = EditModeRoleItemProps | ManagementModeRoleItemProps

const RoleItem: React.FC<RoleItemProps> = (props) => {
  const { t } = useTranslation()
  const { control } = useFormContext()
  if (props.mode === 'edit') {
    const { role, checked, disabled = false, padding = '0px' } = props
    return (
      <RoleItemCheckbox
        role={role}
        locked={disabled}
        hidden={false}
        label={t(`auth.admin.role.${role}`)}
        checked={checked}
        value={checked ? 'true' : 'false'}
        tooltip={t(`auth.admin.role.tooltip.${role}`)}
        padding={padding}
        control={control}
      />
    )
  }

  const { role, state, onChange } = props

  const handleClick = () => {
    const nextState: RoleState = state === 'unchanged' ? 'add' : state === 'add' ? 'remove' : 'unchanged'
    onChange(role, nextState)
  }

  const toggleState = state === 'unchanged' ? 'neutral' : state
  const isActive = state !== 'unchanged'

  return (
    <Stack 
      direction="row" 
      alignItems="center" 
      justifyContent="space-between" 
      onClick={handleClick}
      sx={{ 
        py: 0.25,
        px: 1,
        cursor: 'pointer',
        borderRadius: '4px',
        minHeight: '28px',
        '&:hover': {
          backgroundColor: '#f0f0f0'
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
        <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
          <Typography 
            component="span"
            variant="body2" 
            sx={{ 
              fontSize: '12px',
              lineHeight: 1.2,
              color: isActive ? '#292929' : '#7f93a4',
              wordBreak: 'break-word'
            }}
          >
            {t(`auth.admin.role.${role}`)}
          </Typography>

          <Tooltip title={t(`auth.admin.auth.tooltip.${role}`)} placement="right" arrow>
            <i
              className="fa-regular fa-circle-info"
              style={{
                color: isActive ? '#7f93a4' : '#D1D5DB',
                fontSize: '12px',
                flexShrink: 0
              }}
            />
          </Tooltip>

        </Box>
      </Box>
      <Box sx={{ minWidth: 'auto', display: 'flex', justifyContent: 'flex-end' }}>
        <ActionToggle
          state={toggleState}
          onChange={handleClick}
          disabled={false}
        />
      </Box>
    </Stack>
  )
}

export default RoleItem